import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { ArrowLeft, Mail, MessageCircle, Plus, Star, Ticket, UserPlus, X } from 'lucide-react';
import { CallBar, ChatThread, playMessage, useVoiceCall, type CallApi, type CallInfo, type ChatMessage } from '@ck/ui';
import { apiBase, callRelayUrl, dateTimeLabel } from '@ck/sdk';
import { api, session } from './api';
import { Badge, Empty, Failed, Loading, Modal, Note, PageHead, useAction, useApi } from './ui';

interface Topic { id: string; name: string; about: string }
interface ChatRow { id: string; ticket: string; channel: 'chat' | 'ticket' | 'email'; topic: string; topicName: string; subject: string; status: string; updated_at: string; unread: number; rating: number | null }
interface ChatView {
  chat: { id: string; ticket: string; channel: string; topic: string; topicName: string; subject: string; status: string; rating: number | null; clientComment: string | null; createdAt: string; closedAt: string | null };
  messages: ChatMessage[]; members: { name: string; department: string }[];
  requests: { id: string; department: string; departmentName: string; status: string }[];
}

const CHANNEL: Record<string, { label: string; icon: JSX.Element; help: string }> = {
  chat: { label: 'Live chat', icon: <MessageCircle />, help: 'Talk to customer service now. You can also call from the chat.' },
  ticket: { label: 'Support ticket', icon: <Ticket />, help: 'Describe the problem in detail. We reply here, usually within a few hours.' },
  email: { label: 'Email us', icon: <Mail />, help: 'Write an email from here. It gets a ticket number and replies come here and to your inbox.' },
};
const STATUS: Record<string, string> = { waiting: 'Waiting for reply', open: 'Open', closed: 'Closed' };

/** Live chat, tickets and email to CK, all in one place with a ticket number. */
export function Support({ base = '/v1/support' }: { base?: string } = {}) {
  const list = useApi(() => api.get<{ chats: ChatRow[] }>(`${base}/chats`));
  const topics = useApi(() => api.get<{ topics: Topic[] }>(`${base}/topics`).then((r) => r.topics));
  const [open, setOpen] = useState<string | null>(() => { try { const v = sessionStorage.getItem('ck.support.open'); sessionStorage.removeItem('ck.support.open'); return v; } catch { return null; } });
  const [starting, setStarting] = useState<'chat' | 'ticket' | 'email' | null>(null);
  useEffect(() => { const t = window.setInterval(list.reload, 8000); return () => clearInterval(t); }, []);
  const chats = list.data?.chats ?? [];
  return (
    <>
      <PageHead kicker="Help" title="Support" lead="Chat with us live, open a ticket or email us. Every conversation gets a ticket number. Closed conversations are cleared from here; our team keeps the record." />
      <div className="support-start">
        {(['chat', 'ticket', 'email'] as const).map((c) => (
          <button key={c} className="team-card" onClick={() => setStarting(c)}><b>{CHANNEL[c]!.icon}{CHANNEL[c]!.label}</b><small>{CHANNEL[c]!.help}</small></button>
        ))}
      </div>
      {list.error ? <Failed error={list.error} retry={list.reload} /> : !list.data ? <Loading /> : (
        <div className={`desk${open ? ' has-open' : ''}`}>
          <div className="desk-list">
            {chats.length === 0 && <Empty title="No conversations yet.">Start a live chat or open a ticket above.</Empty>}
            {chats.map((c) => (
              <button key={c.id} className={`desk-item${open === c.id ? ' on' : ''}`} onClick={() => setOpen(c.id)}>
                <b><span>{c.subject}</span>{c.unread > 0 && <span className="desk-dot">{c.unread}</span>}</b>
                <small>{c.ticket} · {CHANNEL[c.channel]?.label ?? 'Chat'} · {STATUS[c.status] ?? c.status}</small>
                <small>{c.topicName} · {dateTimeLabel(c.updated_at)}</small>
              </button>
            ))}
          </div>
          <div className="desk-main">
            {open ? <Conversation base={base} id={open} topics={topics.data ?? []} back={() => { setOpen(null); list.reload(); }} onChange={list.reload} />
              : <Empty title="Pick a conversation">Or start a new one above.</Empty>}
          </div>
        </div>
      )}
      {starting && topics.data && <Start base={base} channel={starting} topics={topics.data} onClose={(id) => { setStarting(null); if (id) { list.reload(); setOpen(id); } }} />}
    </>
  );
}

function Start({ base, channel, topics, onClose }: { base: string; channel: 'chat' | 'ticket' | 'email'; topics: Topic[]; onClose: (id?: string) => void }) {
  const [f, setF] = useState({ topic: topics[0]?.id ?? 'support', subject: '', message: '' });
  const a = useAction();
  async function go(e: FormEvent) {
    e.preventDefault();
    const r = await a.run(() => api.post<{ id: string; ticket: string }>(`${base}/chats`, { ...f, channel }));
    if (r) onClose(r.id);
  }
  return (
    <Modal title={CHANNEL[channel]!.label} onClose={() => onClose()}>
      <form className="form" onSubmit={go}>
        <div className="lbl">Who do you need?
          <div className="team-list">{topics.map((t) => (
            <button type="button" key={t.id} className={`team-card${f.topic === t.id ? ' on' : ''}`} aria-pressed={f.topic === t.id} onClick={() => setF({ ...f, topic: t.id })}><b>{t.name}</b><small>{t.about}</small></button>
          ))}</div>
          <small>Customer service replies first and brings in the team you chose.</small>
        </div>
        <label>{channel === 'email' ? 'Email subject' : 'Subject'}<input required minLength={3} maxLength={140} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="For example: My M-Pesa payment did not go through" /></label>
        <label>{channel === 'chat' ? 'Your first message' : 'Describe what happened'}<textarea required rows={channel === 'chat' ? 3 : 7} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder={channel === 'chat' ? 'Hi, I need help with…' : 'What happened, when, your account or order number, and what you expected.'} /></label>
        {a.error && <Note kind="err">{a.error}</Note>}
        <div className="row-a"><button type="button" className="link" onClick={() => onClose()}>Cancel</button><button className="btn" disabled={a.busy}>{channel === 'chat' ? 'Start chat' : channel === 'email' ? 'Send email' : 'Open ticket'}</button></div>
      </form>
    </Modal>
  );
}

function Conversation({ base, id, topics, back, onChange }: { base: string; id: string; topics: Topic[]; back: () => void; onChange: () => void }) {
  const d = useApi(() => api.get<ChatView>(`${base}/chats/${id}`), [id]);
  const [asking, setAsking] = useState(false);
  const [rate, setRate] = useState<{ rating: number; comment: string }>({ rating: 0, comment: '' });
  const a = useAction();
  useEffect(() => { const t = window.setInterval(() => { void api.get<ChatView>(`${base}/chats/${id}`).then((x) => d.setData(x)).catch(() => undefined); }, 3000); return () => clearInterval(t); }, [id]);
  const callApi: CallApi = useMemo(() => ({
    side: 'client',
    relayUrl: (cid) => callRelayUrl(apiBase(import.meta.env.VITE_API_URL), cid, 'client', session.get()),
    iceServers: () => api.get<{ iceServers: RTCIceServer[] }>(`${base}/ice-servers`).then((r) => r.iceServers),
    current: () => api.get<{ call: CallInfo | null }>(`${base}/chats/${id}/call`).then((r) => r.call),
    get: (cid) => api.get<{ call: CallInfo }>(`${base}/calls/${cid}`).then((r) => r.call),
    start: (offer) => api.post<{ id: string }>(`${base}/chats/${id}/call`, { offer }),
    answer: (cid, sdp) => api.post(`${base}/calls/${cid}/answer`, { answer: sdp }),
    ice: (cid, candidate) => api.post(`${base}/calls/${cid}/ice`, { candidate }),
    end: (cid) => api.post(`${base}/calls/${cid}/end`, {}),
    diag: (cid, events) => api.post(`${base}/calls/${cid}/diag`, { events }),
  }), [id]);
  const closed = d.data?.chat.status === 'closed';
  const voice = useVoiceCall(callApi, !!d.data && !closed);
  const heard = useRef<number | null>(null);
  useEffect(() => {
    const n = d.data?.messages.filter((m) => m.kind !== 'client').length;
    if (n === undefined) return;
    if (heard.current !== null && n > heard.current) playMessage();
    heard.current = n;
  }, [d.data?.messages.length]);
  if (d.error) return <Failed error={d.error} retry={d.reload} />;
  if (!d.data) return <Loading />;
  const { chat, messages, members, requests } = d.data;
  const typing = (d.data as unknown as { typing?: string | null }).typing ?? null;
  async function send(text: string, file: File | null) {
    if (file) {
      const form = new FormData(); form.append('body', text); form.append('file', file);
      const ok = await a.run(() => api.post(`${base}/chats/${id}/files`, form));
      if (ok) { d.reload(); onChange(); } return !!ok;
    }
    const ok = await a.run(() => api.post(`${base}/chats/${id}/messages`, { body: text }));
    if (ok) d.reload();
    return !!ok;
  }
  const pending = requests.filter((r) => r.status === 'pending').map((r) => r.department);
  return (
    <>
      <div className="desk-head">
        <div>
          <button className="link back only-m" onClick={back}><ArrowLeft />All conversations</button>
          <h2>{chat.subject}</h2>
          <small className="muted">Ticket {chat.ticket} · {chat.topicName} · {STATUS[chat.status] ?? chat.status}</small>
        </div>
        {!closed && <div className="row-a">
          <button className="btn ghost sm" onClick={() => setAsking(true)}><UserPlus />Ask for a team</button>
          <button className="btn ghost sm" onClick={async () => { if (confirm('Close this conversation? It will be removed from your list. Our team keeps a record in case you need it later.') && await a.run(() => api.post(`${base}/chats/${id}/close`, {}))) { d.reload(); onChange(); } }}><X />Close</button>
        </div>}
      </div>
      {members.length > 0 && <div className="desk-people">{members.map((m, i) => <span key={i}>{m.name} · {m.department}</span>)}</div>}
      {!closed && <CallBar v={voice} who="support" />}
      {a.error && <Note kind="err">{a.error}</Note>}
      <ChatThread messages={messages} side="client" onSend={send} typing={typing} onTyping={() => { void api.post(`${base}/chats/${id}/typing`, {}).catch(() => undefined); }} disabled={closed} disabledText="This conversation is closed. Start a new one if you need more help."
        loadFile={(mid) => api.blobUrl(`${base}/files/${mid}`)} placeholder="Write a message. Press Enter to send." />
      {closed && (chat.rating ? <Note>Thanks for your feedback. This conversation is closed and has been removed from your list.</Note> : (
        <form className="panel form" onSubmit={async (e) => { e.preventDefault(); if (!rate.rating) { a.setError('Pick a star rating.'); return; } if (await a.run(() => api.post(`${base}/chats/${id}/feedback`, rate))) back(); }}>
          <h3 className="sub-h" style={{ margin: 0 }}>How did we do?</h3>
          <span className="stars pick">{[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" aria-label={`${n} stars`} className={n <= rate.rating ? 'on' : ''} onClick={() => setRate({ ...rate, rating: n })}><Star /></button>)}</span>
          <label>Comment <small>Optional</small><textarea rows={3} value={rate.comment} onChange={(e) => setRate({ ...rate, comment: e.target.value })} placeholder="What went well, or what we should do better" /></label>
          <div className="row-a"><button type="button" className="link" onClick={back}>Skip</button><button className="btn">Send feedback</button></div>
        </form>
      ))}
      {asking && (
        <Modal title="Ask for another team" onClose={() => setAsking(false)}>
          <div className="form">
            <p className="fine">Customer service approves the request and invites the team. You will see when someone joins.</p>
            <div className="team-list">{topics.filter((t) => t.id !== 'support' && t.id !== 'p-support').map((t) => (
              <button key={t.id} className="team-card" disabled={pending.includes(t.id)} onClick={async () => { if (await a.run(() => api.post(`${base}/chats/${id}/request`, { topic: t.id }))) { setAsking(false); d.reload(); } }}>
                <b>{t.name}{pending.includes(t.id) && <Badge status="pending" />}</b><small>{t.about}</small>
              </button>
            ))}</div>
          </div>
        </Modal>
      )}
    </>
  );
}
